/*
 * NavUpdates - product-specific required variant selections.
 * Add-to-cart compatibility fix v2.3 (2026-10-29).
 *
 * Rule A: Option 1 manually selected; options 2 and 3 auto-selected.
 * Rule B: Options 1 and 2 manually selected; option 3 auto-selected.
 * Valid ?variant=ID deep links preselect all three options.
 *
 * The disabled, empty-value "Please Select" placeholders inserted by
 * product-variant-options.liquid determine which options are mandatory.
 * Only applies to variant-selects pickers with exactly three dropdowns.
 *
 * Fix: Do not latch a `waiting` flag until the entire variant-selects
 * element is replaced. Shopify may update its contents in place. Instead,
 * only unlock purchase when the CURRENT selected variant's option values,
 * availability, and the actual product-form variant ID all agree.
 * Preserve the initial required-selection rule even if an AJAX section
 * response omits the Liquid placeholder options.
 * Crucial: do NOT send old automatically-selected option-value IDs to
 * Shopify when a required dropdown changes. Shopify must resolve a new
 * compatible variant for the new manual selection(s).
 */
(() => {
  if (window.__nuRequiredVariantsLoaded) return;
  window.__nuRequiredVariantsLoaded = true;

  const states = new Map();
  // Snapshot this only on initial page load, before Shopify rewrites the URL.
  const initialLinkedVariantId = new URLSearchParams(window.location.search).get('variant');

  const selectsFor = (picker) =>
    Array.from(picker.querySelectorAll('.product-form__input--dropdown select'));

  const requiredIndicesFor = (picker) => {
    const selects = selectsFor(picker);
    if (selects.length !== 3) return [];
    const hasPlaceholder = (i) => Array.from(selects[i].options).some(
      (option) => option.value === '' && option.disabled
    );
    if (!hasPlaceholder(0)) return [];
    return hasPlaceholder(1) ? [0, 1] : [0];
  };

  const stateKey = (picker) =>
    `${picker.dataset.section}:${picker.dataset.productId || ''}`;

  const isManaged = (picker) => {
    if (!picker || picker.id !== `variant-selects-${picker.dataset.section}`) return false;
    // Ignore hidden outgoing sections during Shopify's AJAX replacement.
    for (let node = picker; node; node = node.parentElement) {
      if (node.style?.display === 'none') return false;
    }
    if (selectsFor(picker).length !== 3) return false;
    // Some Shopify section responses omit the placeholders once the variant
    // is selected. Keep the original rule after initial detection rather
    // than abandoning the form while the Add to Cart button is still inert.
    return requiredIndicesFor(picker).length > 0 ||
      Boolean(states.get(stateKey(picker))?.required?.length);
  };

  const managedPickers = () =>
    Array.from(document.querySelectorAll('variant-selects')).filter(isManaged);

  const getState = (picker) => {
    const key = stateKey(picker);
    if (!states.has(key)) {
      states.set(key, {
        values: ['', '', ''],
        required: requiredIndicesFor(picker),
        picker: null,
        selects: [],
        optionNodes: []
      });
    }
    return states.get(key);
  };

  const getForm = (picker) => {
    const formId = selectsFor(picker)[0]?.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  };

  const syncSelectedAttributes = (selects) => {
    selects.forEach((select) => {
      Array.from(select.options).forEach((option) => {
        if (option.selected) {
          if (!option.hasAttribute('selected')) option.setAttribute('selected', 'selected');
        } else if (option.hasAttribute('selected')) {
          option.removeAttribute('selected');
        }
      });
    });
  };

  const getSelectedVariant = (picker) => {
    try {
      return JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  const hydrateLinkedVariant = (picker, selects, state) => {
    if (!initialLinkedVariantId) return;
    const variant = getSelectedVariant(picker);
    if (!variant || String(variant.id) !== initialLinkedVariantId) return;
    if (!Array.isArray(variant.options) || variant.options.length !== selects.length) return;

    const linkedValues = variant.options.map(String);
    const allOptionsExist = linkedValues.every((value, i) =>
      Array.from(selects[i].options).some((option) => option.value === value && !option.disabled)
    );
    if (!allOptionsExist) return;

    selects.forEach((select, i) => { select.value = linkedValues[i]; });
    state.required.forEach((i) => {
      state.values[i] = linkedValues[i];
    });
  };

  const isReady = (picker, state) => {
    const selects = selectsFor(picker);
    const variant = getSelectedVariant(picker);
    const form = getForm(picker);
    const variantInput = form?.querySelector('input[name="id"]');
    const addButton = form?.querySelector('.product-form__submit');

    // Required options must genuinely have been chosen by the customer or
    // validated through a deep-linked Shopify variant. Never use defaults
    // to satisfy those mandatory selections.
    const requiredChosen = state.required.every((i) =>
      Boolean(state.values[i]) && selects[i]?.value === state.values[i]
    );

    // Shopify sometimes replaces only the INNER HTML of variant-selects.
    // The selected-variant JSON must correspond to every current dropdown,
    // not a stale variant left over from the prior selection.
    const variantMatchesSelections =
      Array.isArray(variant?.options) &&
      variant.options.length === selects.length &&
      variant.options.every((value, i) => String(value) === selects[i].value);

    return Boolean(
      requiredChosen &&
      variantMatchesSelections &&
      variant?.available === true &&
      variantInput &&
      !variantInput.disabled &&
      addButton &&
      !addButton.disabled &&
      String(variant.id) === variantInput.value
    );
  };

  const updatePicker = (picker) => {
    const state = getState(picker);
    const selects = selectsFor(picker);

    // Detect whole-picker replacement, select replacement, OR inner option
    // replacement inside an existing <select> (all occur in AJAX themes).
    const replaced = state.picker !== picker ||
      state.selects.length !== selects.length ||
      selects.some((select, i) =>
        state.selects[i] !== select ||
        state.optionNodes[i]?.length !== select.options.length ||
        Array.from(select.options).some((option, j) => state.optionNodes[i]?.[j] !== option)
      );

    if (replaced) {
      // Only an explicit valid initial URL deep link can fill requirements.
      if (state.picker === null) hydrateLinkedVariant(picker, selects, state);
      state.picker = picker;
      state.selects = selects.slice();
      state.optionNodes = selects.map((select) => Array.from(select.options));

      // Treat Shopify's returned selection as authoritative. v2.2 wrote
      // the OLD manual value back over the updated server HTML, which could
      // make the visible dropdown disagree with the returned variant/ID.
      // If Shopify changed a required option, invalidate its confirmation
      // and require the customer to choose it again, rather than forcing
      // an inconsistent combination or silently accepting a default.
      state.required.forEach((i) => {
        if (!state.values[i]) return;
        if (selects[i]?.value !== state.values[i]) {
          state.values[i] = '';
          if (Array.from(selects[i]?.options || []).some(
            (option) => option.value === '' && option.disabled
          )) selects[i].value = '';
        }
      });
      syncSelectedAttributes(selects);
    }

    const ready = isReady(picker, state);
    const form = getForm(picker);
    const addButton = form?.querySelector('.product-form__submit');
    const quickCheckout = form?.querySelector('.shopify-payment-button');

    if (addButton) {
      // Inert only enforces OUR required selections. Native Shopify sold-out
      // and disabled state remains authoritative and is never overwritten.
      if (addButton.inert !== !ready) addButton.inert = !ready;
      addButton.classList.toggle('nu-requires-variant', !ready);
    }
    if (quickCheckout) {
      const desiredDisplay = ready ? '' : 'none';
      if (quickCheckout.style.display !== desiredDisplay) {
        quickCheckout.style.display = desiredDisplay;
      }
    }
  };

  const refresh = () => managedPickers().forEach(updatePicker);

  // Capture customer actions ahead of the theme. Suppress Shopify's AJAX
  // variant fetch until all mandatory choices have been provided.
  document.addEventListener('change', (event) => {
    const element = event.target;
    if (!(element instanceof HTMLSelectElement)) {
      if (element instanceof HTMLInputElement && element.name === 'id') {
        queueMicrotask(refresh);
      }
      return;
    }

    const picker = element.closest('variant-selects');
    if (!isManaged(picker)) return;
    const selects = selectsFor(picker);
    const index = selects.indexOf(element);
    if (index < 0) return;

    const state = getState(picker);
    const required = state.required;
    const wasFirst = state.values[0];

    if (required.includes(index)) state.values[index] = element.value;

    // Rule B: a NEW navigation version requires the customer to confirm
    // region (option 2) again. Retaining the region from the old version
    // can send an invalid combination to Shopify and lock Add to Cart.
    if (index === 0 && required.includes(1) && wasFirst && wasFirst !== element.value) {
      state.values[1] = '';
      const placeholder = Array.from(selects[1].options).find(
        (option) => option.value === '' && option.disabled
      );
      if (placeholder) selects[1].value = '';
    }

    // Dawn reads option[selected] attributes (NOT just select.value) to
    // produce its option_values request. Keep them aligned with the DOM.
    syncSelectedAttributes(selects);

    const complete = required.every((i) =>
      Boolean(state.values[i]) && selects[i].value === state.values[i]
    );

    // v2.2 bug: previously auto-selected options remained in the request
    // after option 1/2 changed. Those now-incompatible IDs could yield a
    // null selected variant, leaving Shopify's native button disabled.
    // On a change to a REQUIRED dropdown, omit every automatic dropdown
    // from the next Shopify request so Shopify can choose a valid fallback.
    // Preserve an explicitly changed automatic dropdown if the customer
    // deliberately adjusted that dropdown instead.
    if (complete && required.includes(index)) {
      selects.forEach((select, i) => {
        if (required.includes(i)) return;
        Array.from(select.options).forEach((option) => {
          if (option.hasAttribute('selected')) option.removeAttribute('selected');
        });
      });
    }

    updatePicker(picker);
    if (!complete) {
      // Do not issue an AJAX request for an incomplete manual selection.
      event.stopImmediatePropagation();
    } else {
      // Dawn's variant-selects handler runs after this capture listener.
      queueMicrotask(refresh);
    }
  }, true);

  // Prevent keyboard/programmatic form submission while choices are missing
  // or while Shopify is still resolving the selected variant.
  document.addEventListener('submit', (event) => {
    const picker = managedPickers().find((candidate) => getForm(candidate) === event.target);
    if (!picker || isReady(picker, getState(picker))) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const selects = selectsFor(picker);
    const state = getState(picker);
    const missing = state.required.find(
      (i) => !state.values[i] || selects[i].value !== state.values[i]
    );
    if (missing !== undefined) selects[missing].focus();
  }, true);

  const start = () => {
    refresh();
    // Child-list changes catch full section swaps AND in-place innerHTML
    // updates. Disabled-attribute changes catch Shopify enabling the button
    // after its async variant update. Do not observe our own inert/class
    // writes, avoiding a MutationObserver feedback loop.
    const observer = new MutationObserver(refresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
